import CloseButton from '../common/CloseButton';
import { BellFilled, ShareAltOutlined } from '@ant-design/icons';
import {
  Spin,
  Dropdown,
  Button as AntButton,
  Input,
  Row,
  Col,
  Typography,
  Divider,
  Select as AntSelect
} from 'antd';
import { Switch } from 'antd';
import moment from 'moment';
import { memo, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import useState from 'use-react-state';
import theme from '../../constants/theme';
import { Button } from '../common/Button';
import { createBroadcast, getBroadcasts, readBroadcast } from '../../utils/api/broadcast';
import { getTeams } from '../../utils/api/team';
import { capitalizeFirstLetter } from '../../utils/file';
import { useRequest, useSocketListeners } from '../../utils/hooks';
import { useUser } from '../../utils/hooks/user';
import Inifinite from '../common/Infinite';
import { Pressable, Text } from '../theme';
import styles from './styles.module.sass';

const { Text: AntText } = Typography;

const Broadcasts = memo(function () {
  const infiniteMessagesRef = useRef();
  const [{ dropdownOpen, hasNew }, setState] = useState({
    dropdownOpen: false,
    hasNew: false
  });

  const toggle = () =>
    setState((s) => {
      s.dropdownOpen = !s.dropdownOpen;
      s.hasNew = false;
    });

  const { teamId, agentId } = useUser(({ team, id } = {}) => ({
    agentId: id,
    teamId: team?.id || team
  }));

  const onBroadcast = (broadcast) => {
    if (broadcast?.agent?.id !== agentId) {
      setState({ hasNew: true });
      toast(
        <>
          <h4 className='font-weight-bolder'>
            {broadcast.agent?.first_name} {broadcast.agent?.last_name}
          </h4>
          {broadcast.message}
        </>,
        {
          position: 'top-center',
          autoClose: broadcast.pinned ? false : 10000
        }
      );
    }
  };

  useSocketListeners({
    listeners: {
      [`Broadcast.created.${teamId}`]: onBroadcast,
      'Broadcast.created': onBroadcast
    }
  });

  const onClose = useCallback(
    async (id, index) => {
      try {
        await readBroadcast(id);
        infiniteMessagesRef.current.setState((s) => {
          s.data.data.splice(index, 1);
        });
      } catch (error) {
        console.log(error);
      }
    },
    [infiniteMessagesRef]
  );

  const queryParams = useMemo(() => [{ limit: 5 }], []);

  const RenderItem = useCallback(
    ({ item: { agent, message, createdAt, id }, index }) => (
      <>
        <div className={styles.notificationItemWrapper}>
          <div className={styles.notificationItem}>
            <Row className={styles.notificationRow}>
              <div className={styles.agentInfoSection}>
                <AntText className={styles.agentName}>
                  {agent.first_name} {agent.last_name}
                </AntText>
                <AntText className={styles.time}>
                  {moment(createdAt).format('MMM D, YYYY hh:mm A')}
                </AntText>
              </div>
              <CloseButton label='Dismiss notification' onClick={() => onClose(id, index)} />
            </Row>
            <AntText className={styles.message}>{message}</AntText>
          </div>
        </div>
        <Divider className={styles.divider} />
      </>
    ),
    [onClose]
  );

  return (
    <Dropdown
      open={dropdownOpen}
      onOpenChange={toggle}
      popupRender={() => (
        <div className={styles.dropdownContainer}>
          <div className={styles.broadcastHead}>
            <AntText className={styles.broadcastHeadText}>Notification</AntText>
          </div>
          <Inifinite
            ref={infiniteMessagesRef}
            params={queryParams}
            RenderItem={RenderItem}
            name='notifications'
            style={{ height: 350 }}
            asyncRequest={getBroadcasts}
            className={styles.scroller}
            disableEndReached={false}
            disableBeginReached={true}
            loadOnMount={dropdownOpen}
          />
        </div>
      )}
      trigger={['click']}
      placement='bottomRight'>
      <AntButton className={styles.bellButtonStyle}>
        {hasNew && <div className={styles.redDot} />}
        <BellFilled style={{ color: '#000', fontSize: '18px' }} />
      </AntButton>
    </Dropdown>
  );
});

export const CreateBroadcast = memo(function () {
  const [{ dropdownOpen, pinned, message, teams, isLoading }, setState] = useState({
    dropdownOpen: false,
    pinned: false,
    isLoading: false,
    message: '',
    teams: undefined
  });

  const toggle = () =>
    setState((s) => {
      s.dropdownOpen = !s.dropdownOpen;
    });

  const {
    state: { data: { data = [] } = {} }
  } = useRequest({ asyncRequest: getTeams, name: 'teams', loadOnMount: dropdownOpen }, [
    dropdownOpen
  ]);

  const options = useMemo(
    () => data.map(({ name, id }) => ({ value: id, label: capitalizeFirstLetter(name) })),
    [data]
  );

  const handleChange = useCallback(
    (values) =>
      setState({
        teams: values || []
      }),
    []
  );

  const onPin = useCallback((pinned) => setState({ pinned }));

  const onChangeInput = useCallback(({ target: { value: message } }) => setState({ message }));

  const onCreateBroadcast = async () => {
    try {
      setState({ isLoading: true });
      await createBroadcast({ message, pinned, teams });
      setState({
        dropdownOpen: false,
        isLoading: false,
        pinned: false,
        message: '',
        teams: undefined
      });
    } catch (error) {
      console.log(error);
      toggle();
    }
  };

  return (
    <Dropdown
      open={dropdownOpen}
      onOpenChange={toggle}
      popupRender={() => (
        <div className={styles.broadcastMessagePanel}>
          <div className={styles.broadcastHead}>
            <AntText className={styles.broadcastHeadText}>Broadcast Message</AntText>
          </div>
          <div className={styles.broadcastFormContainer}>
            <AntText className={styles.broadcastLabel}>Share a message with your agent</AntText>
            <Input.TextArea
              className={styles.messageInput}
              name='message'
              placeholder='Type your message here...'
              value={message}
              onChange={onChangeInput}
              rows={4}
            />
            <div className={styles.teamSection}>
              <AntText className={styles.teamLabel}>Team</AntText>
              <AntSelect
                mode='multiple'
                placeholder='Select team...'
                className={styles.antSelectContainer}
                value={teams || []}
                onChange={handleChange}
                options={options}
                optionRender={(option) => (
                  <span style={{ color: (teams || []).includes(option.value) ? '#fff' : undefined }}>
                    {option.label}
                  </span>
                )}
              />
            </div>
            <div className={styles.pinSection}>
              <Switch checked={pinned} onChange={onPin} style={{ marginRight: 8 }} />
              <AntText className={styles.pinLabel}>Pin</AntText>
            </div>
            <div className={styles.buttonGroup}>
              <Button onClick={toggle} variant='secondary' className={styles.cancelButton}>
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={onCreateBroadcast}
                loading={isLoading}
                disabled={isLoading}>
                {isLoading ? 'Broadcasting...' : 'Broadcast'}
              </Button>
            </div>
          </div>
        </div>
      )}
      trigger={['click']}
      placement='bottomRight'>
      <AntButton className={styles.shareButton}>
        <ShareAltOutlined style={{ color: '#000', fontSize: '18px' }} />
      </AntButton>
    </Dropdown>
  );
});

export default Broadcasts;
